/**
 * Laboratorio de calibración de umbrales.
 *
 * El problema que resuelve: ¿dónde empieza «brusco»? Los umbrales heredados
 * (2,5 m/s², 8 °/s) vienen de telemetría de 1–2 s y no significan nada a la
 * cadencia real de esta flota. Escoger otros a ojo sería cambiar un número
 * arbitrario por otro con mejor letra.
 *
 * El método es el de siempre en estas cosas, y es honesto porque no inventa una
 * verdad física: **un evento es brusco cuando es inusual para esta flota**. Se
 * mide la distribución real de aceleración y giro sobre miles de tramos, y el
 * umbral se pone en un percentil alto. Si el percentil 95 de la aceleración es
 * 1,4 m/s², entonces 1,4 m/s² es lo que separa al 5 % más brusco del resto — y
 * eso sí se puede sostener frente a un cliente.
 *
 * Tres reglas que hacen que esto sea calibración y no numerología:
 *
 *  1. **Una distribución por régimen de cadencia.** Mezclar equipos de 5 s con
 *     los de 10 s produce un percentil que no describe a ninguno de los dos.
 *  2. **Se declara la muestra.** Cuántos vehículos, cuántos tramos, qué
 *     período. Un percentil sobre 200 tramos de un solo carro no es la flota.
 *  3. **El percentil es una decisión de negocio, no técnica.** El p95 marca
 *     como brusco al 5 % de las maniobras; el p99, al 1 %. Cuál se escoge
 *     depende de a cuántos clientes se les quiere hablar, y eso lo decide
 *     Grupo Arcos, no el código.
 *
 * Todo aquí es puro: mismas mediciones, mismos umbrales propuestos, siempre.
 */

import type { TunableThresholds } from "../contracts/api.ts";
import type { CandidateMeasurements } from "../pipeline/driving-analysis-v5.ts";
import type { AnalysisProfile, CadenceRegime } from "../pipeline/profiles.ts";

export const CALIBRATION_LAB_VERSION = "guardia-calibration-v1" as const;

/** Muestra mínima para que un percentil signifique algo. */
export const MINIMUM_SAMPLE = 500;

/** Percentiles que se reportan siempre, para poder ver la forma de la curva. */
export const REPORTED_PERCENTILES = [50, 75, 90, 95, 97.5, 99, 99.5] as const;

export type Distribution = {
  count: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  /** Percentil → valor. Interpolación lineal entre los dos vecinos. */
  percentiles: Record<string, number | null>;
  /** `false` cuando la muestra es demasiado chica para leer el percentil. */
  sufficient: boolean;
};

const round = (value: number) => Math.round(value * 1000) / 1000;

/**
 * Percentil con interpolación lineal (método R-7, el de las hojas de cálculo).
 * Se escoge ese y no otro para que un analista pueda reproducir el número en
 * Excel con `PERCENTIL()` y le dé lo mismo. Si no se puede comprobar a mano,
 * no es auditable.
 */
export function percentile(sorted: number[], p: number): number | null {
  if (!sorted.length) return null;
  if (sorted.length === 1) return sorted[0]!;
  const rank = (p / 100) * (sorted.length - 1);
  const low = Math.floor(rank);
  const high = Math.ceil(rank);
  if (low === high) return sorted[low]!;
  return sorted[low]! + (rank - low) * (sorted[high]! - sorted[low]!);
}

export function describe(values: number[]): Distribution {
  const sorted = [...values].sort((a, b) => a - b);
  const percentiles: Record<string, number | null> = {};
  for (const p of REPORTED_PERCENTILES) {
    const value = percentile(sorted, p);
    percentiles[String(p)] = value === null ? null : round(value);
  }
  return {
    count: sorted.length,
    min: sorted.length ? round(sorted[0]!) : null,
    max: sorted.length ? round(sorted[sorted.length - 1]!) : null,
    mean: sorted.length ? round(sorted.reduce((sum, v) => sum + v, 0) / sorted.length) : null,
    percentiles,
    sufficient: sorted.length >= MINIMUM_SAMPLE,
  };
}

/** Las tres muestras de detección, ordenadas de menor a mayor. */
export type FamilySamples = {
  /** Aceleración positiva, m/s². */
  acceleration: number[];
  /** Frenada en magnitud, m/s². */
  braking: number[];
  /** Velocidad angular en magnitud, °/s. */
  yaw: number[];
};

/**
 * De qué mediciones se hace la muestra de cada familia. **Un solo sitio.**
 *
 * Tres consumidores tienen que ver exactamente lo mismo: la tira de
 * distribución que se dibuja, el botón que propone un percentil y la guarda que
 * veta al guardar. Mientras cada uno armaba su muestra por su cuenta, el
 * Laboratorio propuso un p99 de giro de 18 °/s —la media vuelta de 180° exactos
 * dividida por los 10 s de cadencia, o sea ruido de rumbo— y el servidor rechazó
 * ese mismo número contra un p99 de 1,98 sobre la muestra del motor. Los dos
 * números salían del mismo botón.
 *
 * La entrada es lo que mide el motor (`measureCandidateValues`), que ya aplica
 * las exclusiones del detector: los pares de 180° exactos y los tramos
 * demasiado cortos para que el rumbo signifique algo. Derivar la muestra de
 * `inspectDriving().pairs` —que existe para explicar fila por fila y por eso NO
 * las aplica— es lo que produjo ese 18.
 */
export function familySamples(
  measurements: Pick<CandidateMeasurements, "longitudinalMps2" | "yawDegreesPerSecond">,
): FamilySamples {
  const ascending = (values: number[]) => [...values].sort((a, b) => a - b);
  return {
    acceleration: ascending(measurements.longitudinalMps2.filter((value) => value > 0)),
    braking: ascending(measurements.longitudinalMps2.filter((value) => value < 0).map(Math.abs)),
    yaw: ascending(measurements.yawDegreesPerSecond.map(Math.abs)),
  };
}

const round3 = (value: number | null) => (value === null ? null : Math.round(value * 1000) / 1000);

/**
 * Los umbrales que el Laboratorio propone al pedir un percentil de UN vehículo.
 *
 * Vive en el núcleo y no en la pantalla por dos razones. La primera es que así
 * se puede probar la propiedad que importa —que lo que este botón propone pasa
 * la guarda del servidor— sin montar el panel. La segunda es que la pantalla ya
 * tenía su propia copia de «qué mediciones son la muestra de cada familia», y
 * esa copia es el hallazgo A.
 *
 * Solo mueve los tres umbrales de detección y sus bandas. Las compuertas
 * secundarias y el límite de velocidad no salen de un percentil: son decisiones
 * de política que el analista escribe, y proponerlas aquí sería inventarlas.
 */
export function proposeThresholdsAtPercentile(input: {
  measurements: Pick<CandidateMeasurements, "longitudinalMps2" | "yawDegreesPerSecond">;
  /** Percentil del umbral de detección. Decisión de negocio. */
  percentileUsed: number;
  /** Lo que hay puesto ahora: se conserva lo que el percentil no alcance. */
  current: TunableThresholds;
}): TunableThresholds {
  const { acceleration, braking, yaw } = familySamples(input.measurements);
  const q = input.percentileUsed;

  const a = round3(percentile(acceleration, q));
  const b = round3(percentile(braking, q));
  const y = round3(percentile(yaw, q));

  return {
    ...input.current,
    accelerationThresholdMps2: a ?? input.current.accelerationThresholdMps2,
    brakingThresholdMps2: b === null ? input.current.brakingThresholdMps2 : -b,
    minimumYawRateDegreesPerSecond: y ?? input.current.minimumYawRateDegreesPerSecond,
    severityAcceleration: bandFrom(acceleration, [q, 99, 99.5], input.current.severityAcceleration),
    severityBraking: bandFrom(braking, [q, 99, 99.5], input.current.severityBraking),
    severityYawRate: bandFrom(yaw, [q, 99, 99.5], input.current.severityYawRate),
  };
}

/** Percentiles de una muestra ordenada como banda de tres, con reserva. */
function bandFrom(
  sorted: number[],
  quantiles: [number, number, number],
  fallback: readonly [number, number, number],
): [number, number, number] {
  const at = (q: number, index: number) => round3(percentile(sorted, q)) ?? fallback[index]!;
  return [at(quantiles[0], 0), at(quantiles[1], 1), at(quantiles[2], 2)];
}

/** Una corrida de mediciones de un vehículo, lista para entrar a la muestra. */
export type CalibrationSample = {
  deviceId: string;
  plate: string | null;
  /** Cadencia mediana medida de ese vehículo. Decide a qué régimen pertenece. */
  medianIntervalSeconds: number | null;
  measurements: CandidateMeasurements;
};

export type RegimeCalibration = {
  regime: CadenceRegime;
  vehicles: number;
  /** Aceleración positiva, m/s². */
  acceleration: Distribution;
  /** Frenada como valor absoluto, m/s². */
  braking: Distribution;
  yawRate: Distribution;
  segmentSpeedKmh: Distribution;
  intervalSeconds: Distribution;
};

export type ThresholdProposal = {
  regime: CadenceRegime;
  percentileUsed: number;
  accelerationThresholdMps2: number | null;
  brakingThresholdMps2: number | null;
  minimumYawRateDegreesPerSecond: number | null;
  /** Bandas [moderada, alta, crítica] en los percentiles indicados. */
  severityPercentiles: readonly [number, number, number];
  accelerationBands: readonly (number | null)[];
  brakingBands: readonly (number | null)[];
  yawBands: readonly (number | null)[];
  /** `false` si alguna distribución no llegó a la muestra mínima. */
  usable: boolean;
  warnings: string[];
};

export type CalibrationReport = {
  version: typeof CALIBRATION_LAB_VERSION;
  sampleVehicles: number;
  regimes: RegimeCalibration[];
  proposals: ThresholdProposal[];
  /** Muestras que no se pudieron clasificar por no tener cadencia medible. */
  unclassified: number;
};

/** El régimen se decide por la cadencia medida, no por lo que diga el equipo. */
function regimeOf(medianIntervalSeconds: number | null): CadenceRegime | null {
  if (medianIntervalSeconds === null || !Number.isFinite(medianIntervalSeconds)) return null;
  if (medianIntervalSeconds < 3) return "HIGH";
  if (medianIntervalSeconds < 7) return "MEDIUM";
  return "LOW";
}

function buildProposal(
  calibration: RegimeCalibration,
  percentileUsed: number,
  severityPercentiles: readonly [number, number, number],
): ThresholdProposal {
  const warnings: string[] = [];
  const at = (distribution: Distribution, p: number) => {
    const key = String(p);
    return distribution.percentiles[key] ?? null;
  };

  if (!calibration.acceleration.sufficient) {
    warnings.push(
      `Aceleración: solo ${calibration.acceleration.count} mediciones, por debajo de las ${MINIMUM_SAMPLE} mínimas.`,
    );
  }
  if (!calibration.braking.sufficient) {
    warnings.push(
      `Frenada: solo ${calibration.braking.count} mediciones, por debajo de las ${MINIMUM_SAMPLE} mínimas.`,
    );
  }
  if (!calibration.yawRate.sufficient) {
    warnings.push(
      `Giro: solo ${calibration.yawRate.count} mediciones, por debajo de las ${MINIMUM_SAMPLE} mínimas.`,
    );
  }
  if (calibration.vehicles < 5) {
    warnings.push(
      `Solo ${calibration.vehicles} vehículos en la muestra: el percentil describe a esos, no a la flota.`,
    );
  }

  const braking = at(calibration.braking, percentileUsed);

  return {
    regime: calibration.regime,
    percentileUsed,
    accelerationThresholdMps2: at(calibration.acceleration, percentileUsed),
    // La frenada se mide en absoluto y se propone con signo, como la usa el motor.
    brakingThresholdMps2: braking === null ? null : -braking,
    minimumYawRateDegreesPerSecond: at(calibration.yawRate, percentileUsed),
    severityPercentiles,
    accelerationBands: severityPercentiles.map((p) => at(calibration.acceleration, p)),
    brakingBands: severityPercentiles.map((p) => {
      const value = at(calibration.braking, p);
      return value === null ? null : value;
    }),
    yawBands: severityPercentiles.map((p) => at(calibration.yawRate, p)),
    usable:
      calibration.acceleration.sufficient &&
      calibration.braking.sufficient &&
      calibration.yawRate.sufficient &&
      calibration.vehicles >= 5,
    warnings,
  };
}

export function buildCalibrationReport(input: {
  samples: CalibrationSample[];
  /** Percentil del umbral de detección. Decisión de negocio; por defecto p95. */
  percentileUsed?: number;
  /** Percentiles de las bandas de severidad. Por defecto p95 / p99 / p99.5. */
  severityPercentiles?: readonly [number, number, number];
}): CalibrationReport {
  const percentileUsed = input.percentileUsed ?? 95;
  const severityPercentiles = input.severityPercentiles ?? ([95, 99, 99.5] as const);

  const buckets = new Map<CadenceRegime, CalibrationSample[]>();
  let unclassified = 0;

  for (const sample of input.samples) {
    const regime = regimeOf(sample.medianIntervalSeconds);
    if (!regime) {
      unclassified++;
      continue;
    }
    const existing = buckets.get(regime);
    if (existing) existing.push(sample);
    else buckets.set(regime, [sample]);
  }

  // Orden fijo: el informe de calibración tiene que ser comparable entre corridas.
  const order: CadenceRegime[] = ["HIGH", "MEDIUM", "LOW"];
  const regimes = order
    .filter((regime) => buckets.has(regime))
    .map((regime) => {
      const samples = buckets.get(regime)!;
      // La misma definición de muestra por familia que usan la pantalla y la
      // guarda, aquí sobre la unión de los vehículos del régimen.
      const families = familySamples({
        longitudinalMps2: samples.flatMap((s) => s.measurements.longitudinalMps2),
        yawDegreesPerSecond: samples.flatMap((s) => s.measurements.yawDegreesPerSecond),
      });
      return {
        regime,
        vehicles: samples.length,
        acceleration: describe(families.acceleration),
        braking: describe(families.braking),
        yawRate: describe(families.yaw),
        segmentSpeedKmh: describe(samples.flatMap((s) => s.measurements.segmentSpeedKmh)),
        intervalSeconds: describe(samples.flatMap((s) => s.measurements.midpointIntervalSeconds)),
      };
    });

  return {
    version: CALIBRATION_LAB_VERSION,
    sampleVehicles: input.samples.length,
    regimes,
    proposals: regimes.map((regime) =>
      buildProposal(regime, percentileUsed, severityPercentiles),
    ),
    unclassified,
  };
}

/**
 * Aplica una propuesta sobre un perfil y devuelve el perfil calibrado.
 *
 * No muta el perfil de entrada, y una propuesta inutilizable devuelve `null`
 * en vez de un perfil a medias: aplicar umbrales calculados sobre una muestra
 * insuficiente es exactamente lo que este módulo existe para evitar.
 */
export function applyProposal(
  profile: AnalysisProfile,
  proposal: ThresholdProposal,
): AnalysisProfile | null {
  if (!proposal.usable) return null;
  if (
    proposal.accelerationThresholdMps2 === null ||
    proposal.brakingThresholdMps2 === null ||
    proposal.minimumYawRateDegreesPerSecond === null
  ) {
    return null;
  }
  const bands = (values: readonly (number | null)[]): readonly [number, number, number] | null =>
    values.every((value): value is number => value !== null)
      ? [values[0]!, values[1]!, values[2]!]
      : null;

  const accelerationBands = bands(proposal.accelerationBands);
  const brakingBands = bands(proposal.brakingBands);
  const yawBands = bands(proposal.yawBands);
  if (!accelerationBands || !brakingBands || !yawBands) return null;

  return {
    ...profile,
    version: profile.version + 1,
    longitudinal: {
      ...profile.longitudinal,
      accelerationThresholdMps2: proposal.accelerationThresholdMps2,
      brakingThresholdMps2: proposal.brakingThresholdMps2,
    },
    turns: {
      ...profile.turns,
      minimumYawRateDegreesPerSecond: proposal.minimumYawRateDegreesPerSecond,
    },
    severity: {
      ...profile.severity,
      acceleration: accelerationBands,
      braking: brakingBands,
      yawRate: yawBands,
    },
    calibration: "CALIBRATED",
    measures:
      `${profile.measures} Umbrales calibrados en el percentil ${proposal.percentileUsed} ` +
      `de la flota (${CALIBRATION_LAB_VERSION}).`,
  };
}
